/**
 * Settings > Account > Representative details (G-28).
 *
 * What the attorney (or accredited representative) puts on a G-28 and the
 * preparer section of other USCIS forms. The form fills read it as the
 * `attorney_profile` source and fill the mapped fields in code, so a bar
 * number is never retyped by the model. Saved as one section.
 */
import { Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect, useState, type FC, type ReactNode } from "react";
import { toast } from "sonner";

import { LawyerAPI } from "@/api/lawyerApi";
import type {
  BarAdmission,
  LawyerDTO,
  OfficeAddress,
  RepresentativeProfile,
  UnitType
} from "@/api/types/lawFirm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ListCard } from "@/components/ui/list-card";
import { SectionHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

type Props = {
  lawyerId: string | null;
  profile: RepresentativeProfile | null | undefined;
  onSaved: (lawyer: LawyerDTO) => void;
};

const NO_UNIT = "none";

const UNIT_LABELS: Record<UnitType, string> = {
  apt: "Apt.",
  ste: "Ste.",
  flr: "Flr."
};

/** The section's editable shape: every field present, blanks as "". */
export const draftOf = (
  profile: RepresentativeProfile | null | undefined
): RepresentativeProfile => ({
  uscis_online_account_number: profile?.uscis_online_account_number ?? "",
  office_address: { ...(profile?.office_address ?? {}) },
  daytime_phone: profile?.daytime_phone ?? "",
  mobile_phone: profile?.mobile_phone ?? "",
  email: profile?.email ?? "",
  fax: profile?.fax ?? "",
  bar_admissions: (profile?.bar_admissions ?? []).map((bar) => ({ ...bar })),
  eoir_id: profile?.eoir_id ?? "",
  subject_to_restricting_order: Boolean(profile?.subject_to_restricting_order),
  restricting_order_explanation: profile?.restricting_order_explanation ?? "",
  is_accredited_representative: Boolean(profile?.is_accredited_representative),
  recognized_organization: profile?.recognized_organization ?? "",
  accreditation_date: profile?.accreditation_date ?? "",
  is_law_student: Boolean(profile?.is_law_student),
  supervising_attorney: profile?.supervising_attorney ?? ""
});

/** What is saved: bar rows without an authority are dropped. */
export const payloadOf = (
  draft: RepresentativeProfile
): RepresentativeProfile => ({
  ...draft,
  bar_admissions: (draft.bar_admissions ?? []).filter((bar) =>
    bar.licensing_authority.trim()
  )
});

/** A label-left field row: the label names the row's first control. */
const FieldRow: FC<{ id: string; label: string; children: ReactNode }> = ({
  id,
  label,
  children
}) => (
  <div className="flex items-start gap-4 px-4 py-2.5">
    <Label
      htmlFor={id}
      className="w-34 shrink-0 pt-1.5 text-base leading-4.5 font-normal text-fg-2"
    >
      {label}
    </Label>
    <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
  </div>
);

const Toggle: FC<{
  id: string;
  label: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}> = ({ id, label, checked, onChange }) => (
  <div className="flex items-center justify-between gap-6 px-4 py-3">
    <Label htmlFor={id} className="text-base font-normal text-fg">
      {label}
    </Label>
    <Switch id={id} checked={checked} onCheckedChange={onChange} />
  </div>
);

export const RepresentativeDetailsSection: FC<Props> = ({
  lawyerId,
  profile,
  onSaved
}) => {
  const [draft, setDraft] = useState<RepresentativeProfile>(draftOf(profile));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(draftOf(profile));
  }, [profile]);

  const set = <K extends keyof RepresentativeProfile>(
    key: K,
    value: RepresentativeProfile[K]
  ) => setDraft((current) => ({ ...current, [key]: value }));

  const setAddress = <K extends keyof OfficeAddress>(
    key: K,
    value: OfficeAddress[K]
  ) =>
    setDraft((current) => ({
      ...current,
      office_address: { ...(current.office_address ?? {}), [key]: value }
    }));

  const setBar = (index: number, patch: Partial<BarAdmission>) =>
    setDraft((current) => ({
      ...current,
      bar_admissions: (current.bar_admissions ?? []).map((bar, i) =>
        i === index ? { ...bar, ...patch } : bar
      )
    }));

  const addBar = () =>
    setDraft((current) => ({
      ...current,
      bar_admissions: [
        ...(current.bar_admissions ?? []),
        { licensing_authority: "", bar_number: "" }
      ]
    }));

  const removeBar = (index: number) =>
    setDraft((current) => ({
      ...current,
      bar_admissions: (current.bar_admissions ?? []).filter(
        (_, i) => i !== index
      )
    }));

  const save = async () => {
    if (!lawyerId) return;
    setSaving(true);
    try {
      const lawyer = await LawyerAPI.update(lawyerId, {
        representative_profile: payloadOf(draft)
      });
      onSaved(lawyer);
      toast.success("Representative details saved");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not save the representative details."
      );
    } finally {
      setSaving(false);
    }
  };

  const address = draft.office_address ?? {};
  const bars = draft.bar_admissions ?? [];

  return (
    <section
      className="flex w-full flex-col gap-3"
      data-testid="representative-details"
    >
      <SectionHeader
        title="Representative Details (G-28)"
        description="Filled into the G-28 and the preparer section of other forms."
      />

      <ListCard>
        <FieldRow id="rep-uscis-account" label="USCIS Online Account Number">
          <Input
            id="rep-uscis-account"
            value={draft.uscis_online_account_number ?? ""}
            onChange={(e) => set("uscis_online_account_number", e.target.value)}
          />
        </FieldRow>
        <FieldRow id="rep-street" label="Office Address">
          <Input
            id="rep-street"
            placeholder="Street"
            value={address.street ?? ""}
            onChange={(e) => setAddress("street", e.target.value)}
          />
        </FieldRow>
        <FieldRow id="rep-unit-type" label="Unit">
          <Select
            value={address.unit_type ?? NO_UNIT}
            onValueChange={(value) =>
              setAddress(
                "unit_type",
                value === NO_UNIT ? null : (value as UnitType)
              )
            }
          >
            <SelectTrigger id="rep-unit-type" className="w-24 shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_UNIT}>None</SelectItem>
              {(Object.keys(UNIT_LABELS) as UnitType[]).map((unit) => (
                <SelectItem key={unit} value={unit}>
                  {UNIT_LABELS[unit]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            id="rep-unit-number"
            aria-label="Unit Number"
            placeholder="Unit number"
            value={address.unit_number ?? ""}
            onChange={(e) => setAddress("unit_number", e.target.value)}
          />
        </FieldRow>
        <FieldRow id="rep-city" label="City, State, ZIP">
          <Input
            id="rep-city"
            placeholder="City"
            value={address.city ?? ""}
            onChange={(e) => setAddress("city", e.target.value)}
          />
          <Input
            id="rep-state"
            aria-label="State"
            placeholder="State"
            className="w-22 shrink-0"
            value={address.state ?? ""}
            onChange={(e) => setAddress("state", e.target.value)}
          />
          <Input
            id="rep-zip"
            aria-label="ZIP Code"
            placeholder="ZIP code"
            className="w-24 shrink-0"
            value={address.zip_code ?? ""}
            onChange={(e) => setAddress("zip_code", e.target.value)}
          />
        </FieldRow>
        <FieldRow id="rep-province" label="Province, Postal Code">
          <Input
            id="rep-province"
            placeholder="Province"
            value={address.province ?? ""}
            onChange={(e) => setAddress("province", e.target.value)}
          />
          <Input
            id="rep-postal"
            aria-label="Postal Code"
            placeholder="Postal code"
            className="w-24 shrink-0"
            value={address.postal_code ?? ""}
            onChange={(e) => setAddress("postal_code", e.target.value)}
          />
        </FieldRow>
        <FieldRow id="rep-country" label="Country">
          <Input
            id="rep-country"
            value={address.country ?? ""}
            onChange={(e) => setAddress("country", e.target.value)}
          />
        </FieldRow>
        <FieldRow id="rep-daytime-phone" label="Daytime Phone">
          <Input
            id="rep-daytime-phone"
            value={draft.daytime_phone ?? ""}
            onChange={(e) => set("daytime_phone", e.target.value)}
          />
        </FieldRow>
        <FieldRow id="rep-mobile-phone" label="Mobile Phone">
          <Input
            id="rep-mobile-phone"
            value={draft.mobile_phone ?? ""}
            onChange={(e) => set("mobile_phone", e.target.value)}
          />
        </FieldRow>
        <FieldRow id="rep-email" label="Email">
          <Input
            id="rep-email"
            type="email"
            value={draft.email ?? ""}
            onChange={(e) => set("email", e.target.value)}
          />
        </FieldRow>
        <FieldRow id="rep-fax" label="Fax">
          <Input
            id="rep-fax"
            value={draft.fax ?? ""}
            onChange={(e) => set("fax", e.target.value)}
          />
        </FieldRow>
        <FieldRow id="rep-eoir" label="EOIR ID">
          <Input
            id="rep-eoir"
            value={draft.eoir_id ?? ""}
            onChange={(e) => set("eoir_id", e.target.value)}
          />
        </FieldRow>
      </ListCard>

      <SectionHeader
        className="mt-4"
        title="Bar Admissions"
        actions={
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={addBar}
            data-testid="rep-add-bar"
          >
            <Plus />
            Add Bar
          </Button>
        }
      />
      {bars.length ? (
        <ListCard>
          {bars.map((bar, index) => (
            <FieldRow
              key={index}
              id={`rep-bar-authority-${index}`}
              label="Licensing Authority"
            >
              <Input
                id={`rep-bar-authority-${index}`}
                value={bar.licensing_authority}
                placeholder="State Bar of California"
                onChange={(e) =>
                  setBar(index, { licensing_authority: e.target.value })
                }
              />
              <Input
                id={`rep-bar-number-${index}`}
                aria-label="Bar Number"
                placeholder="Bar number"
                className="w-28 shrink-0"
                value={bar.bar_number ?? ""}
                onChange={(e) => setBar(index, { bar_number: e.target.value })}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Remove Bar Admission"
                onClick={() => removeBar(index)}
              >
                <Trash2 />
              </Button>
            </FieldRow>
          ))}
        </ListCard>
      ) : null}

      <ListCard className="mt-4">
        <Toggle
          id="rep-restricting-order"
          label="I am subject to an order restricting my practice of law"
          checked={Boolean(draft.subject_to_restricting_order)}
          onChange={(on) => set("subject_to_restricting_order", on)}
        />
        {draft.subject_to_restricting_order ? (
          <FieldRow id="rep-restricting-explanation" label="Explanation">
            <Textarea
              id="rep-restricting-explanation"
              value={draft.restricting_order_explanation ?? ""}
              onChange={(e) =>
                set("restricting_order_explanation", e.target.value)
              }
            />
          </FieldRow>
        ) : null}
        <Toggle
          id="rep-accredited"
          label="I am an accredited representative"
          checked={Boolean(draft.is_accredited_representative)}
          onChange={(on) => set("is_accredited_representative", on)}
        />
        {draft.is_accredited_representative ? (
          <>
            <FieldRow id="rep-organization" label="Recognized Organization">
              <Input
                id="rep-organization"
                value={draft.recognized_organization ?? ""}
                onChange={(e) => set("recognized_organization", e.target.value)}
              />
            </FieldRow>
            <FieldRow id="rep-accreditation-date" label="Accreditation Date">
              <Input
                id="rep-accreditation-date"
                type="date"
                value={draft.accreditation_date ?? ""}
                onChange={(e) => set("accreditation_date", e.target.value)}
              />
            </FieldRow>
          </>
        ) : null}
        <Toggle
          id="rep-law-student"
          label="I am a law student or law graduate"
          checked={Boolean(draft.is_law_student)}
          onChange={(on) => set("is_law_student", on)}
        />
        {draft.is_law_student ? (
          <FieldRow id="rep-supervising" label="Supervising Attorney">
            <Input
              id="rep-supervising"
              value={draft.supervising_attorney ?? ""}
              onChange={(e) => set("supervising_attorney", e.target.value)}
            />
          </FieldRow>
        ) : null}
      </ListCard>

      <div className="flex justify-end">
        <Button
          variant="primary"
          type="button"
          onClick={() => void save()}
          disabled={saving || !lawyerId}
          data-testid="rep-save"
        >
          {saving ? <Loader2 className="animate-spin" /> : null}
          Save Representative Details
        </Button>
      </div>
    </section>
  );
};
